import { useUntrustedData } from '@auth0/auth0-acul-react/login-id';
import BaseWrapper from 'creative-ui/oktaComponents/BaseWrapper';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTransactionLocale } from '@/hooks/useTranslationLocale';
import { trackPageView } from '@/lib/tagging';
import { EmailForm } from '@/screens/login-id/components/EmailForm';
import { FranceConnectLinkForm } from '@/screens/login-id/components/FranceConnectLinkForm';
import { LandingForm } from '@/screens/login-id/components/LandingForm';
import { PasskeyInfoForm } from '@/screens/login-id/components/PasskeyInfoForm';
import { useLoginIdManager } from '@/screens/login-id/hooks/useLoginIdManager';
import {
  clearFranceConnectLinkCookie,
  isFranceConnectLinkFlow,
} from '@/screens/login-id/utils/franceConnectLinkState';

type ScreenView =
  | 'landing'
  | 'email'
  | 'passkeyInfo'
  | 'passkeyLoginInfo'
  | 'franceConnectLink';

// Main Login ID Screen
function LoginIdScreen() {
  // If returning from login-password,
  // Auth0 provides the previously submitted username via untrustedData.
  const untrustedData = useUntrustedData();
  const hasReturnedUsername = !!untrustedData?.submittedFormData?.username;

  // Captured once at mount: the SPA dropped a short-lived signal cookie
  // on `.engie.fr` before launching this transaction. We consume it
  // immediately so it never bleeds into a subsequent, unrelated flow.
  const [isFranceConnectLink] = useState<boolean>(() => {
    const present = isFranceConnectLinkFlow();
    if (present) clearFranceConnectLinkCookie();
    return present;
  });

  const [currentView, setCurrentView] = useState<ScreenView>(() => {
    if (isFranceConnectLink) return 'franceConnectLink';
    if (hasReturnedUsername) return 'email';
    return 'landing';
  });

  const { t } = useTranslation(['loginId', 'passkeyEnrollment']);

  useTransactionLocale();

  useEffect(() => {
    switch (currentView) {
      case 'landing':
        trackPageView('login');
        break;
      case 'email':
        trackPageView('login/username');
        break;
      case 'passkeyInfo':
        trackPageView('mfa/passkey/info');
        break;
      case 'passkeyLoginInfo':
        trackPageView('mfa/passkey/login-info');
        break;
      case 'franceConnectLink':
        trackPageView('login/franceconnect-link');
        break;
    }
  }, [currentView]);

  const landingTitle = t('loginId:landingForm.title');
  const emailTitle = t('loginId:emailForm.title');
  const passkeyTitle = t('passkeyEnrollment:header');
  const passkeyDetail = t('passkeyEnrollment:info');
  const franceConnectLinkTitle = t('loginId:franceConnectLinkForm.title');
  const franceConnectLinkBody = t('loginId:franceConnectLinkForm.body');

  document.title = t('pageTitle');

  const { handlePasskeyLogin } = useLoginIdManager();

  const handleBack = () => {
    setCurrentView('landing');
  };

  const handleLogin = () => {
    setCurrentView('email');
  };

  const handleFranceConnectLinkConnectWithEmail = () => {
    setCurrentView('email');
  };

  const renderScreen = (
    title: string,
    body: string | undefined,
    content: React.ReactNode,
  ) => (
    <BaseWrapper>
      <h1 className="u-heading-2 u-textCenter u-margin-b-l">{title}</h1>
      {body ? <p className="u-textCenter u-margin-b-l">{body}</p> : null}
      {content}
    </BaseWrapper>
  );

  switch (currentView) {
    case 'franceConnectLink':
      return renderScreen(
        franceConnectLinkTitle,
        franceConnectLinkBody,
        <FranceConnectLinkForm
          onConnectWithEmail={handleFranceConnectLinkConnectWithEmail}
        />,
      );

    case 'landing':
      return renderScreen(
        landingTitle,
        undefined,
        <LandingForm
          onShowEmail={() => setCurrentView('email')}
          onShowPasskeyInfo={() => setCurrentView('passkeyInfo')}
          onShowPasskeyLoginInfo={() => setCurrentView('passkeyLoginInfo')}
        />,
      );

    case 'passkeyInfo':
      return renderScreen(
        passkeyTitle,
        passkeyDetail,
        <PasskeyInfoForm onBack={handleBack} onLogin={handleLogin} />,
      );

    case 'passkeyLoginInfo':
      return renderScreen(
        passkeyTitle,
        passkeyDetail,
        <PasskeyInfoForm
          onBack={handleBack}
          onLogin={handleLogin}
          onPasskeyLogin={handlePasskeyLogin}
        />,
      );

    case 'email':
      return renderScreen(
        emailTitle,
        undefined,
        <EmailForm onBack={handleBack} />,
      );
  }
}

export default LoginIdScreen;
